import { useRouter } from "next/router"
import React, { useEffect, useRef, useState } from "react"
import useSWRInfinite from "swr/infinite"
import "swiper/css"

import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { useTranslation } from "next-i18next"
import useBBBNetwork from "../hooks/useBBBNetwork"
import { GameSortOptions, getGames, getPlayerData } from "../services/games"
import { PageHead } from "../components/shared/PageHead"
import { Layout } from "../components/shared/Layout"
import { Dropdown } from "../components/forms/dropdown"
import { Loading } from "../components/shared/Loading"
import { GameItem } from "../components/game/GameItem"
import InfiniteScroll from "react-infinite-scroll-component"
import { SearchBox } from "../components/shared/SearchBox"
import { Hashtags } from "../components/shared/Hashtags"
import useHasWindow from "../hooks/useHasWindow"
import useSWR from "swr"
import { formatEtherscanLink, formatTwitterLink, isAddress, shortenHex } from "../utils/string"
import { TwitterSvg, WalletSvg } from "../assets/svg"
import { Copy } from "../components/shared/Copy"
import { formatUnixTimestampLong } from "../utils/date"
import { formatNumber } from "../utils/number"
import clsx from "clsx"
import useClipboard from "react-use-clipboard"
import { getSupportTokens } from "../utils/network"

export async function getServerSideProps({ locale }) {
    return {
        props: {
            ...(await serverSideTranslations(locale, ["common", "my-game", "game-details"]))
        }
    }
}

function Search() {
    const { t: tCommon } = useTranslation("common")

    const { t: tMyGame } = useTranslation("my-game")

    const hasWindow = useHasWindow()

    const router = useRouter()
    const { pathname, query } = router

    const { s: searchString, st, locale, lng, order, ht: hashtags } = query
    const { currentChainId } = useBBBNetwork()
    const {
        chain: { id: chainId }
    } = useBBBNetwork()

    const supportTokens = getSupportTokens(chainId)
    const tokens = [
        { id: "", label: tMyGame("currencypairs") },
        ...supportTokens.map((x) => {
            return {
                id: x.id,
                label: x.label
            }
        })
    ]

    const [status, setStatus] = useState(st ?? "BETTING")
    const [tokenName, setTokenName] = useState("")
    const [language, setLanguage] = useState(lng)
    const [orderBy, setOrderBy] = useState(order)

    const getGameKey = (pageIndex, previousData) => {
        if (!currentChainId) {
            return
        }

        const paginator = previousData && previousData.paginator
        if (paginator && !paginator.hasNextPage) {
            return null
        }

        return [pageIndex + 1, searchString, currentChainId, orderBy, status, language, hashtags, tokenName]
    }

    const fetchGames = async (page) => {
        const response = await getGames(
            orderBy ? GameSortOptions[orderBy.toString()] : GameSortOptions.NONE,
            page,
            10,
            "",
            -1,
            searchString ? searchString.toString() : "",
            null,
            status?.toString(),
            language?.toString(),
            hashtags?.toString(),
            tokenName
        )

        return response.data.data
    }

    const fetchPlayerData = async () => {
        const response = await getPlayerData(searchString)

        return response.data.data
    }

    const { data, error, size, setSize } = useSWRInfinite(getGameKey, fetchGames, {
        revalidateFirstPage: false
    })

    const { data: playerData } = useSWR(isAddress(searchString) && `PLAYER_DATA${searchString}`, fetchPlayerData)

    const isLoading = !data && !error
    const isLoadingMore = isLoading || (size > 0 && data && data[data.length - 1].paginator.hasNextPage)
    const totalItems = data ? data.reduce((previousValue, games) => previousValue + games.list.length, 0) : 0

    const statusRef = useRef(null)
    const languageRef = useRef(null)
    const sortRef = useRef(null)
    const tokenRef = useRef(null)

    const statuses = [
        {
            id: "",
            label: tMyGame("status.all")
        },
        {
            id: "BETTING",
            label: tCommon("status.betting")
        },
        /*{
            id: "ENDED_BETTING",
            label: tCommon("status.endedBetting")
        },
        {
            id: "WAITING_RESULT",
            label: tCommon("status.waitingResult")
        },*/
        {
            id: "ENDED_BETTING",
            label: tCommon("status.endedBetting")
        },
        {
            id: "SETTLED_RESULT",
            label: tCommon("status.settledResult")
        },
        {
            id: "CANCELLED",
            label: tCommon("status.cancelled")
        }
    ]

    const languages = [
        {
            id: "",
            label: tMyGame("languages")
        },
        {
            id: "en",
            label: tCommon("setting.english-long")
        },
        {
            id: "zh",
            label: tCommon("setting.chinese-long")
        }
    ]

    const sortOptions = [
        {
            id: "",
            label: tCommon("sort.by")
        },
        {
            id: "TRENDING",
            label: tCommon("sort.trending")
        },
        {
            id: "NEW",
            label: tCommon("sort.new")
        },
        {
            id: "LIQUIDITY",
            label: tCommon("sort.liquidity")
        },
        {
            id: "EXPIRING",
            label: tCommon("sort.expiring")
        }
    ]

    const onSelectStatus = (item) => {
        setStatus(item.id)

        router.push(
            {
                pathname,
                query: { ...query, st: item.id }
            },
            undefined,
            { locale: locale?.toString(), shallow: true }
        )
    }

    const onSetLanguage = (item) => {
        setLanguage(item.id)

        router.push(
            {
                pathname,
                query: { ...query, lng: item.id }
            },
            undefined,
            { locale: locale?.toString(), shallow: true }
        )
    }

    const onSetSort = (item) => {
        setOrderBy(item.id)

        router.push(
            {
                pathname,
                query: { ...query, order: item.id }
            },
            undefined,
            { locale: locale?.toString(), shallow: true }
        )
    }

    const selectedStatus = statuses.find((x) => x.id === status)
    const selectedLanguage = languages.find((x) => x.id === language)
    const selectedSort = sortOptions.find((x) => x.id === orderBy)

    useEffect(() => {
        const selectedLanguage = languages.find((x) => x.id === language)
        if (languageRef && languageRef.current) {
            if (selectedLanguage) {
                languageRef.current.setSelected(selectedLanguage)
            } else {
                languageRef.current.setSelected(languages[0])
            }
        }
    }, [language])

    const [isCopied, setCopied] = useClipboard(playerData?.player?.walletAddress, { successDuration: 2000 })

    return (
        <Layout>
            <PageHead
                data={{
                    title: `BBB - Search`
                }}
            />

            <div className="mx-auto mt-5 max-w-[1100px]">
                <Hashtags />
            </div>
            {playerData && playerData.player && (
                <div
                    className={clsx(
                        "mx-auto mt-[20px] h-[142px] max-w-[336px] rounded-[20px] border-[0.5px] border-[#ADADAD] bg-[#F6F6F6] py-[17px] px-[16px] lg:h-[188px] lg:max-w-[1208px] lg:py-[21px] lg:pl-[47px]",
                        playerData.player.setting.showGameData && "h-[442px]"
                    )}
                >
                    <div className={"flex flex-col lg:flex-row lg:items-center"}>
                        <div className={"flex"}>
                            <div className={"h-[87px] w-[87px] rounded-full bg-[#E6E6E6] lg:h-[145px] lg:w-[145px]"}>
                                {playerData.player.profileImage && (
                                    <img
                                        src={playerData.player.profileImage}
                                        className={"h-full w-full rounded-full object-cover"}
                                    />
                                )}
                            </div>
                            <div className={"ml-[20px] lg:ml-[31px]"}>
                                <div className={"text-18 font-bold leading-[22px] lg:text-20 lg:leading-6"}>
                                    {playerData.player.name}
                                </div>
                                <div className={"mt-[13px] flex items-center lg:mt-5"}>
                                    <a
                                        onClick={setCopied}
                                        href={formatEtherscanLink("Account", [
                                            currentChainId,
                                            playerData.player.walletAddress
                                        ])}
                                        target={"_blank"}
                                        rel={"noreferrer"}
                                        className={"group"}
                                    >
                                        <WalletSvg
                                            width={16}
                                            height={14}
                                            className={
                                                "fill-[#546F7A] group-hover:fill-accent-1 last-child:fill-[#546F7A] group-hover:last-child:fill-accent-1"
                                            }
                                        />
                                    </a>
                                    <div
                                        className={
                                            "ml-[15px] mr-[12px] text-12 font-medium leading-[17px] text-[#0F84F0] lg:text-14"
                                        }
                                    >
                                        <a
                                            onClick={setCopied}
                                            href={formatEtherscanLink("Account", [
                                                currentChainId,
                                                playerData.player.walletAddress
                                            ])}
                                            target={"_blank"}
                                            rel={"noreferrer"}
                                            className={"hover:text-accent-1"}
                                        >
                                            {shortenHex(playerData.player.walletAddress)}
                                        </a>
                                    </div>
                                    <Copy text={playerData.player.walletAddress} />
                                </div>
                                {playerData.player.twitter && (
                                    <div className={"mt-[12px] flex items-center lg:mt-[15px]"}>
                                        <a
                                            href={formatTwitterLink(playerData.player.twitter.username)}
                                            target={"_blank"}
                                            rel={"noreferrer"}
                                            className={"group"}
                                        >
                                            <TwitterSvg
                                                width={18}
                                                height={14}
                                                className={"fill-[#546F7A] group-hover:fill-accent-1"}
                                            />
                                        </a>
                                        <div
                                            className={
                                                "ml-[15px] mr-[12px] text-12 font-medium leading-[17px] lg:text-14"
                                            }
                                        >
                                            <a
                                                href={formatTwitterLink(playerData.player.twitter.username)}
                                                className={"hover:text-accent-1"}
                                            >
                                                {playerData.player.twitter.username}
                                            </a>
                                        </div>
                                    </div>
                                )}
                                <div className={"mt-[12px] text-12 text-[#808080] lg:mt-[15px]"}>
                                    {tCommon("joinedOn", {
                                        date: formatUnixTimestampLong(playerData.player.createdAt)
                                    })}
                                </div>
                            </div>
                        </div>
                        {playerData.player.setting.showGameData && (
                            <>
                                <div
                                    className={"my-[25px] h-1 w-full border-t-[0.5px] border-[#D0D0D0] lg:hidden"}
                                ></div>
                                <div
                                    className={
                                        "grid grid-cols-[auto_auto] gap-y-[20px] px-5 pl-5 lg:ml-[56px] lg:h-[145px] lg:grid-cols-[auto_auto_auto] lg:gap-x-[55px] lg:pl-0 lg:pr-0"
                                    }
                                >
                                    <div>
                                        <div
                                            className={"text-25 font-bold leading-[25px] lg:text-28 lg:leading-[34px]"}
                                        >
                                            {formatNumber(playerData.totalCreatedGame)}
                                        </div>
                                        <div
                                            className={
                                                "text-12 font-medium leading-[15px] text-[#737373] lg:text-14 lg:leading-[17px]"
                                            }
                                        >
                                            {tCommon("statistics.createGame")}
                                        </div>
                                    </div>
                                    <div>
                                        <div
                                            className={"text-25 font-bold leading-[25px] lg:text-28 lg:leading-[34px]"}
                                        >
                                            {formatNumber(playerData.totalCancelledGame)}
                                        </div>
                                        <div
                                            className={
                                                "text-12 font-medium leading-[15px] text-[#737373] lg:text-14 lg:leading-[17px]"
                                            }
                                        >
                                            {tCommon("statistics.cancelledGames")}
                                        </div>
                                    </div>
                                    <div>
                                        <div
                                            className={"text-25 font-bold leading-[25px] lg:text-28 lg:leading-[34px]"}
                                        >
                                            {formatNumber(playerData.completionRate)}%
                                        </div>
                                        <div
                                            className={
                                                "text-12 font-medium leading-[15px] text-[#737373] lg:text-14 lg:leading-[17px]"
                                            }
                                        >
                                            {tCommon("statistics.completionRate")}
                                        </div>
                                    </div>
                                    <div>
                                        <div
                                            className={"text-25 font-bold leading-[25px] lg:text-28 lg:leading-[34px]"}
                                        >
                                            {formatNumber(playerData.totalParticipant)}
                                        </div>
                                        <div
                                            className={
                                                "text-12 font-medium leading-[15px] text-[#737373] lg:text-14 lg:leading-[17px]"
                                            }
                                        >
                                            {tCommon("statistics.totalParticipants")}
                                        </div>
                                    </div>
                                    <div>
                                        <div
                                            className={"text-25 font-bold leading-[25px] lg:text-28 lg:leading-[34px]"}
                                        >
                                            {formatNumber(playerData.totalBetAmount)}{" "}
                                            <span className={"text-20 font-normal"}>USD</span>
                                        </div>
                                        <div
                                            className={
                                                "text-12 font-medium leading-[15px] text-[#737373] lg:text-14 lg:leading-[17px]"
                                            }
                                        >
                                            {tCommon("statistics.totalBettingAmount")}
                                        </div>
                                    </div>
                                    <div>
                                        <div
                                            className={"text-25 font-bold leading-[25px] lg:text-28 lg:leading-[34px]"}
                                        >
                                            {formatNumber(playerData.totalProfitLoss)}{" "}
                                            <span className={"text-20 font-normal"}>USD</span>
                                        </div>
                                        <div
                                            className={
                                                "text-12 font-medium leading-[15px] text-[#737373] lg:text-14 lg:leading-[17px]"
                                            }
                                        >
                                            {tCommon("statistics.totalPL")}
                                        </div>
                                    </div>
                                </div>
                                <div className={"mt-[20px] ml-5 lg:mt-0 lg:ml-[55px]"}>
                                    <div className={"text-25 font-bold leading-[25px] lg:text-28 lg:leading-[34px]"}>
                                        {formatNumber(playerData.totalPoolAmount)}{" "}
                                        <span className={"text-20 font-normal"}>USD</span>
                                    </div>
                                    <div
                                        className={
                                            "text-12 font-medium leading-[15px] text-[#737373] lg:text-14 lg:leading-[17px]"
                                        }
                                    >
                                        {tCommon("statistics.totalGamePool")}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            <div className="mx-auto mt-5 max-w-[1100px]">
                <div className={"mt-[31px] flex flex-col-reverse flex-wrap items-center justify-between lg:flex-row"}>
                    <div className={"flex w-full items-center justify-between px-4 lg:w-auto lg:justify-start lg:px-0"}>
                        <div className={"mr-2 w-[117px] lg:mt-0 lg:w-[135px]"}>
                            <Dropdown
                                ref={statusRef}
                                afterSelected={onSelectStatus}
                                items={statuses}
                                defaultSelected={selectedStatus}
                                buttonClassNames={
                                    "rounded-[5px] h-[31px] text-12 lg:text-14 lg:h-[36px] font-primary border-[0.5px] border-[rgba(90,90,90,0.5)] hover:bg-white hover:text-black hover:border-accent-1"
                                }
                                labelClassNames={"ml-3"}
                                narrowClassNames={"w-[12px] h-[6px] group-hover:fill-black"}
                                itemClassNames={"hover:text-accent-1 ml-3 text-12 lg:text-14"}
                            />
                        </div>
                        <div className={"mx-[13px] w-[103px] lg:mx-2 lg:mt-0 lg:w-[151px]"}>
                            <Dropdown
                                ref={tokenRef}
                                afterSelected={(item) => setTokenName(item.id)}
                                items={tokens}
                                classNames={"lg:w-[151px]"}
                                buttonClassNames={
                                    "rounded-[5px] h-[31px] lg:h-[36px] text-12 lg:text-14 font-primary border-[0.5px] border-[rgba(90,90,90,0.5)] hover:bg-white hover:text-black hover:border-accent-1"
                                }
                                labelClassNames={"ml-3"}
                                narrowClassNames={"w-[12px] h-[6px] group-hover:fill-black"}
                                itemClassNames={"hover:text-accent-1 ml-3"}
                            />
                        </div>
                        <div className={"mx-[13px] w-[103px] lg:mx-2 lg:mt-0 lg:w-[135px]"}>
                            <Dropdown
                                ref={languageRef}
                                afterSelected={onSetLanguage}
                                items={languages}
                                defaultSelected={selectedLanguage}
                                buttonClassNames={
                                    "rounded-[5px] h-[31px] text-12 lg:text-14 lg:h-[36px] font-primary border-[0.5px] border-[rgba(90,90,90,0.5)] hover:bg-white hover:text-black hover:border-accent-1"
                                }
                                labelClassNames={"ml-3"}
                                narrowClassNames={"w-[12px] h-[6px] text-12 lg:text-14 group-hover:fill-black"}
                                itemClassNames={"hover:text-accent-1 ml-3"}
                            />
                        </div>
                        <div className={"w-[92px] lg:mx-2 lg:mt-0 lg:w-[105px]"}>
                            <Dropdown
                                ref={sortRef}
                                afterSelected={onSetSort}
                                items={sortOptions}
                                defaultSelected={selectedSort}
                                buttonClassNames={
                                    "rounded-[5px] h-[31px] lg:h-[36px] text-12 lg:text-14 font-primary border-[0.5px] border-[rgba(90,90,90,0.5)] hover:bg-white hover:text-black hover:border-accent-1"
                                }
                                labelClassNames={"ml-3"}
                                narrowClassNames={"w-[12px] h-[6px] text-12 lg:text-14 group-hover:fill-black"}
                                itemClassNames={"hover:text-accent-1 ml-3"}
                            />
                        </div>
                    </div>
                    <div className={"w-full px-4 lg:w-auto lg:px-0"}>
                        <SearchBox
                            router={router}
                            className={"ml-0 mb-[15px] block w-full text-12 lg:mb-0 lg:w-[433px] lg:text-14"}
                            classNameInput={"text-14 focus:border-accent-1 search-border-1"}
                        />
                    </div>
                </div>
                <div className={"relative mt-5 px-4 lg:px-0"}>
                    {isLoading && <Loading />}
                    <InfiniteScroll
                        dataLength={totalItems}
                        next={() => setSize(size + 1)}
                        hasMore={isLoadingMore}
                        loader={""}
                        scrollableTarget="body"
                        className={"b-no-scroll grid grid-cols-1 gap-2 lg:grid-cols-1"}
                    >
                        {data &&
                            data.map((gameResponse) => {
                                return gameResponse.list.map((game) => {
                                    return <GameItem game={game} key={game.id} hasWindow={hasWindow} />
                                })
                            })}
                    </InfiniteScroll>

                    {data && data.length === 1 && data[0].paginator.total === 0 && (
                        <div className={"mt-10 text-center"}>
                            <div className={"font-secondary text-[25px] font-bold leading-6 text-white"}>
                                {tCommon(searchString ? "nodata.search.title" : "nodata.category.title")}
                            </div>
                            <div className={"mt-[3px] font-secondary text-base leading-6 text-white"}>
                                {tCommon(searchString ? "nodata.search.description" : "nodata.category.description")}
                            </div>
                            <div
                                className={
                                    "m-auto mt-[14px] h-[141px] w-[308px] bg-[url(../assets/imgs/bg/empty.png)] bg-cover bg-no-repeat"
                                }
                            ></div>
                        </div>
                    )}
                </div>
                <div className={"h-[60px]"}></div>
            </div>
        </Layout>
    )
}

export default Search
